import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_sanmarino-market/artifacts/x01s3ury_Contemporary%20Emblem%20Logo%20for%20SAN%20MARINO%20MARKET.png";
const KEY = "smm_grand_opening_seen_v1";

// Full-screen grand-opening splash: rich red velvet curtains open, logo reveals,
// 5 golden stars (Repubblica di San Marino) arc into place.
export default function GrandOpening() {
  const [stage, setStage] = useState("idle"); // idle | opening | revealed | leaving | done
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Show only once per browser
    if (localStorage.getItem(KEY)) return;
    setShow(true);
    let t1, t2;
    // Double RAF: ensure DOM paints with stage="idle" (curtains closed) BEFORE
    // transitioning to "opening" — otherwise CSS transition is skipped.
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        t1 = setTimeout(() => setStage("opening"), 350);
        t2 = setTimeout(() => setStage("revealed"), 2300);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (t1) clearTimeout(t1);
      if (t2) clearTimeout(t2);
    };
  }, []);

  const dismiss = () => {
    setStage("leaving");
    setTimeout(() => {
      try { localStorage.setItem(KEY, JSON.stringify({ at: new Date().toISOString() })); } catch (e) {}
      setShow(false);
      setStage("done");
    }, 700);
  };

  if (!show) return null;

  const opening = stage === "opening" || stage === "revealed" || stage === "leaving";
  const revealed = stage === "revealed" || stage === "leaving";
  const leaving = stage === "leaving";

  return (
    <div
      className={`fixed inset-0 z-[500] overflow-hidden bg-[#0b0b0b] transition-opacity duration-700 ${leaving ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      data-testid="grand-opening-overlay"
      role="dialog"
      aria-label="Grand opening San Marino Market"
    >
      {/* STAGE BACKDROP */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1d3d3a] via-[#1a1410] to-[#3a0a0a]" />
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_center,_rgba(255,200,120,0.25),_transparent_60%)]" />

      {/* SPOTLIGHT */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 top-[8%] w-[80vw] max-w-[700px] aspect-[2/1] rounded-full blur-2xl pointer-events-none transition-opacity duration-1000 ${revealed ? "opacity-100" : "opacity-0"}`}
        style={{ background: "radial-gradient(ellipse at center top, rgba(255,220,150,0.5), transparent 65%)" }}
      />

      {/* CONTENT (centered) */}
      <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
        {/* welcome eyebrow */}
        <div className={`text-amber-200/80 text-xs sm:text-sm font-bold uppercase tracking-[0.4em] transition-all duration-700 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"}`}>
          Benvenuto
        </div>

        {/* logo with shine */}
        <div className={`mt-6 relative transition-all duration-1000 ${revealed ? "scale-100 opacity-100" : "scale-50 opacity-0"}`}>
          <div className="absolute inset-0 -m-4 rounded-full bg-amber-300/20 blur-2xl animate-pulse" />
          <img
            src={LOGO_URL}
            alt="San Marino Market"
            className="relative w-32 h-32 sm:w-40 sm:h-40 object-contain drop-shadow-[0_0_30px_rgba(255,200,100,0.5)]"
          />
        </div>

        {/* 5 stars arc */}
        <div className="relative mt-6 h-14 w-72 sm:w-96" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => {
            const positions = [
              { x: "-44%", y: "20%", rot: -20 },
              { x: "-22%", y: "0%", rot: -10 },
              { x: "0%", y: "-8%", rot: 0 },
              { x: "22%", y: "0%", rot: 10 },
              { x: "44%", y: "20%", rot: 20 },
            ];
            const p = positions[i];
            const delay = revealed ? 200 + i * 140 : 0;
            return (
              <span
                key={i}
                className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-700 ease-out ${revealed ? "opacity-100" : "opacity-0"}`}
                style={{
                  transform: revealed
                    ? `translate(calc(-50% + ${p.x}), calc(-50% + ${p.y})) rotate(${p.rot}deg) scale(1)`
                    : `translate(-50%, calc(-50% + 80%)) scale(0.3)`,
                  transitionDelay: `${delay}ms`,
                }}
              >
                <Star5 />
              </span>
            );
          })}
        </div>

        {/* main heading */}
        <h1
          className={`mt-8 text-3xl sm:text-5xl font-black leading-tight transition-all duration-800 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ transitionDelay: "700ms" }}
        >
          <span className="block text-amber-200/70 text-sm sm:text-base font-bold tracking-widest uppercase mb-2">Oggi nasce</span>
          <span className="block bg-gradient-to-br from-amber-200 via-yellow-100 to-amber-400 bg-clip-text text-transparent drop-shadow-[0_4px_24px_rgba(255,200,100,0.25)]">
            SAN MARINO<br />MARKET
          </span>
        </h1>

        {/* tagline */}
        <p
          className={`mt-5 text-amber-50/80 text-sm sm:text-base max-w-md transition-all duration-700 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ transitionDelay: "1100ms" }}
        >
          Il marketplace esclusivo della Repubblica di San Marino
        </p>

        {/* enter button */}
        <button
          onClick={dismiss}
          className={`mt-10 group inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 active:scale-95 text-[#1d3d3a] font-black px-7 h-12 rounded-full shadow-[0_10px_30px_-10px_rgba(255,200,100,0.7)] transition-all duration-500 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
          style={{ transitionDelay: "1400ms" }}
          data-testid="grand-opening-enter"
        >
          <Sparkles className="w-4 h-4 fill-current" />
          Entra nel marketplace
        </button>
      </div>

      {/* CURTAINS */}
      <Curtain side="left" opening={opening} />
      <Curtain side="right" opening={opening} />

      {/* VALANCE (top decorative pelmet) */}
      <div className="absolute top-0 left-0 right-0 h-12 sm:h-16 z-[2] pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-[#4a0a0a] via-[#7a0e0e] to-[#5a0a0a] shadow-[0_8px_24px_rgba(0,0,0,0.6)]" />
        <div className="absolute inset-x-0 bottom-0 h-4 flex">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="flex-1 bg-gradient-to-b from-[#7a0e0e] to-[#3a0505]"
              style={{ clipPath: "polygon(0 0, 100% 0, 50% 100%)" }}
            />
          ))}
        </div>
        {/* golden trim */}
        <div className="absolute left-0 right-0 bottom-3 h-[3px] bg-gradient-to-r from-amber-300/0 via-amber-300/90 to-amber-300/0" />
      </div>
    </div>
  );
}

function Curtain({ side, opening }) {
  const isLeft = side === "left";
  return (
    <div
      aria-hidden="true"
      className="absolute top-0 bottom-0 w-1/2 z-[3] transition-transform duration-[1800ms] ease-[cubic-bezier(0.65,0,0.35,1)] origin-top will-change-transform"
      style={{
        left: isLeft ? 0 : "50%",
        transform: opening
          ? `translateX(${isLeft ? "-105%" : "105%"})`
          : "translateX(0)",
      }}
    >
      {/* Velvet body — vertical pleats via repeating gradient */}
      <div
        className="absolute inset-0 shadow-[0_0_60px_rgba(0,0,0,0.55)]"
        style={{
          background:
            "linear-gradient(180deg, #5b0c0c 0%, #7a0f0f 30%, #4d0808 70%, #2a0404 100%), repeating-linear-gradient(90deg, rgba(0,0,0,0.35) 0px, rgba(0,0,0,0.35) 1px, transparent 1px, transparent 28px), repeating-linear-gradient(90deg, rgba(255,180,180,0.05) 14px, transparent 14px, transparent 28px)",
          backgroundBlendMode: "multiply, normal, screen",
        }}
      />
      {/* Inner edge highlight */}
      <div
        className="absolute top-0 bottom-0 w-6"
        style={{
          [isLeft ? "right" : "left"]: 0,
          background: isLeft
            ? "linear-gradient(90deg, transparent, rgba(0,0,0,0.55))"
            : "linear-gradient(270deg, transparent, rgba(0,0,0,0.55))",
        }}
      />
      {/* Golden tassel trim on inner edge */}
      <div
        className="absolute top-0 bottom-0 w-1"
        style={{
          [isLeft ? "right" : "left"]: 0,
          background: "linear-gradient(180deg, #f5d27a, #b4862a, #f5d27a)",
          boxShadow: "0 0 10px rgba(255,210,120,0.6)",
        }}
      />
    </div>
  );
}

function Star5() {
  // Solid 5-point golden star with subtle inner gradient + glow
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" className="drop-shadow-[0_0_6px_rgba(255,210,120,0.85)]">
      <defs>
        <linearGradient id="smm-star-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff3c2" />
          <stop offset="55%" stopColor="#f7c64a" />
          <stop offset="100%" stopColor="#b4862a" />
        </linearGradient>
      </defs>
      <path
        d="M12 2.6 14.81 8.7l6.69.65-5.02 4.46 1.49 6.54L12 16.9l-5.97 3.45 1.49-6.54L2.5 9.35l6.69-.65L12 2.6Z"
        fill="url(#smm-star-grad)"
        stroke="#7a571b"
        strokeWidth="0.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}
